/**
 * Zod schemas for API payloads and message-body shapes.
 *
 * The `body` column on `messages` is a JSON blob whose shape depends
 * on `kind`. The discriminated union below is the contract.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------- ids
export const Id = z.string().min(1).max(64);

/** List of circle IDs the author wants to share this row with, in
 *  addition to the household. An empty list (or omitted field) means
 *  household-only — the default. */
export const CircleIdsList = z.array(Id).max(20).optional();

/** Discriminated union audience selector. Family-only by default;
 *  circle-scoped posts carry the destination circleId. Posts target
 *  exactly one audience — the old multi-circle `circleIds` field is
 *  deprecated. */
export const Audience = z.discriminatedUnion('type', [
  z.object({ type: z.literal('family') }),
  z.object({ type: z.literal('circle'), circleId: Id }),
]);
export type Audience = z.infer<typeof Audience>;

// --------------------------------------------------------- enum helpers
export const Role = z.enum(['parent', 'youth']);
export type Role = z.infer<typeof Role>;

export const PrayerStatus = z.enum(['active', 'answered', 'archived']);
export type PrayerStatus = z.infer<typeof PrayerStatus>;

export const ReactionKind = z.enum(['praying', 'amen', 'heart']);
export type ReactionKind = z.infer<typeof ReactionKind>;

// -------------------------------------------------------------- message kinds
export const MessageKind = z.enum([
  'text',
  'shared_verse',
  'shared_photo',
  'shared_prayer',
  'shared_encouragement',
]);
export type MessageKind = z.infer<typeof MessageKind>;

/** Reserved mention id meaning "everyone in the author's family." The
 *  @-picker surfaces it as `@myfamily`; when present in a mentions[]
 *  array the server expands it to every member of the actor's family
 *  (minus the actor) before fanning out notifications + pushes. It is a
 *  deliberately non-hex sentinel so it can never collide with a real
 *  member id. */
export const FAMILY_MENTION_ID = '@family';

/** Optional list of member-ids the author @-mentioned, captured by the
 *  picker so the server can emit notifications + pushes. May also carry
 *  the FAMILY_MENTION_ID sentinel for a family-wide mention. `Id` is a
 *  permissive string, so the sentinel validates without a special case. */
export const Mentions = z.array(Id).max(20).optional();
export const TextBody = z.object({
  kind: z.literal('text'),
  text: z.string().min(1),
  mentions: Mentions,
});

export const SharedVerseBody = z.object({
  kind: z.literal('shared_verse'),
  date: z.string(), // YYYY-MM-DD pointer into verses
  ref: z.string(),
  excerpt: z.string(),
});
export const SharedPhotoBody = z.object({
  kind: z.literal('shared_photo'),
  photoId: Id,
  caption: z.string().optional(),
});
export const SharedPrayerBody = z.object({
  kind: z.literal('shared_prayer'),
  prayerId: Id,
});
export const SharedEncouragementBody = z.object({
  kind: z.literal('shared_encouragement'),
  encouragementId: Id,
});

export const MessageBody = z.discriminatedUnion('kind', [
  TextBody,
  SharedVerseBody,
  SharedPhotoBody,
  SharedPrayerBody,
  SharedEncouragementBody,
]);
export type MessageBody = z.infer<typeof MessageBody>;

// ------------------------------------------------------------- API payloads
export const CreatePrayer = z.object({
  body: z.string().min(1).max(2000),
  mentions: Mentions,
  audience: Audience.optional(),
});

export const AnswerPrayer = z.object({
  note: z.string().max(2000).optional(),
});

export const CreateMessage = z.object({
  body: MessageBody,
  audience: Audience.optional(),
});

export const PresignPhoto = z.object({
  mimeType: z.string().regex(/^image\/(jpeg|png|webp|heic|heif)$/),
  sizeBytes: z.number().int().positive().max(50_000_000), // 50 MB cap
  /** SHA-256 hex of the original file bytes (64 chars). When set, the
   *  endpoint dedups against existing photos in the caller's family. */
  hash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  /** Destination audience. Needed at presign time so the dedup path can
   *  link an existing photo into the requested circle without a second
   *  round-trip. */
  audience: Audience.optional(),
});

export const FinalizePhoto = z.object({
  r2Key: z.string(),
  takenAt: z.number().int().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  mimeType: z.string(),
  caption: z.string().max(500).optional(),
  mentions: Mentions,
  audience: Audience.optional(),
  /** SHA-256 hex of the original file bytes; persisted so the next
   *  upload of the same file can dedupe at presign time. */
  hash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
});

export const UpdatePhoto = z.object({
  caption: z.string().max(500).nullable(),
  mentions: Mentions,
  /** Set when the user is editing a circle photo from /generations; lets
   *  the server expand the mention-validation roster to include circle
   *  members from other households. Validated via assertWritableCircle. */
  circleId: Id.optional(),
});

export const CreatePhotoComment = z.object({
  body: z.string().min(1).max(2000),
  mentions: Mentions,
  /** Same as UpdatePhoto.circleId — surfaces cross-household mentions
   *  on circle-context comments without changing family-tab behavior. */
  circleId: Id.optional(),
});

export const PushSubscribe = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string(),
    auth: z.string(),
  }),
});

// ----------------------------------------------------------------- circles
/** Which surfaces a circle is allowed to see. */
export const CircleSurfaces = z.object({
  photos: z.boolean().default(true),
  prayer: z.boolean().default(true),
  chat: z.boolean().default(false),
});
export type CircleSurfaces = z.infer<typeof CircleSurfaces>;

export const CreateCircle = z.object({
  name: z.string().min(1).max(80),
  surfaces: CircleSurfaces.optional(),
});

export const UpdateCircle = z.object({
  name: z.string().min(1).max(80).optional(),
  surfaces: CircleSurfaces.optional(),
});

// 6–8 digit numeric PIN. 100× more entropy than the original 4-digit
// floor, with the same numeric-keypad UX. Real security is the
// per-family rate limit in KV (5 wrong attempts → 5 min lock).
//
// Used for CREATING / ROTATING a PIN. The verify-side schemas
// (sign-in, change-PIN's currentPin) use `PinLegacy` so a family
// still on the old 4-digit floor can sign in once and upgrade via
// /me/pin without DB surgery.
export const Pin = z.string().regex(/^\d{6,8}$/, 'PIN must be 6–8 digits');
export const PinLegacy = z.string().regex(/^\d{4,8}$/, 'PIN must be 4–8 digits');

export const StartFamily = z.object({
  familyName: z.string().min(1).max(60),
  displayName: z.string().min(1).max(40),
  role: Role,
  pin: Pin,
  timezone: z.string().default('America/Chicago'),
});

export const JoinFamily = z.object({
  inviteCode: z.string().min(4).max(16),
  displayName: z.string().min(1).max(40),
  role: Role,
  pin: Pin,
  birthdate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

export const SignIn = z.object({
  inviteCode: z.string().min(4).max(16),
  memberId: Id,
  pin: PinLegacy,
});

/** Phase 3 invite-by-email. Owner enters the invitee's display name,
 *  email, and role; server emails a magic link with the token. */
export const CreateInvitation = z.object({
  displayName: z.string().min(1).max(40),
  email: z.string().email().max(254),
  role: Role,
});

/** Owner re-sends an email sign-in link to an EXISTING member (one who
 *  joined via invite-code + PIN and has no email yet). Accepting the
 *  link attaches + verifies this address on that member's account
 *  rather than creating a new member. */
export const SendMemberEmailLink = z.object({
  email: z.string().email().max(254),
});
export type SendMemberEmailLink = z.infer<typeof SendMemberEmailLink>;

/** Posted from /invite/:token when the recipient confirms. No body
 *  fields today (the URL token + the recipient's click are the
 *  consent). Optional displayName override lets the recipient correct
 *  what the inviter typed before the row is created. */
export const AcceptInvitation = z.object({
  displayName: z.string().min(1).max(40).optional(),
});

/** Circle admin invites a whole new family into their circle by email.
 *  displayName is an optional greeting hint (the recipient sets their
 *  real name when they create their household). */
export const CreateCircleInvitation = z.object({
  email: z.string().email().max(254),
  displayName: z.string().min(1).max(40).optional(),
});

/** Posted from /circle-invite/:token to accept. Creates the recipient's
 *  own household (they become its first parent) and auto-joins it to the
 *  inviting circle. Mirrors StartFamily minus the role (always parent). */
export const AcceptCircleInvitation = z.object({
  familyName: z.string().min(1).max(60),
  displayName: z.string().min(1).max(40),
  pin: Pin,
  timezone: z.string().default('America/Chicago'),
});

/** Self-service PIN rotation. The caller proves possession of the
 *  current PIN before we accept a new one — same threat model as
 *  changing a password. */
export const ChangePin = z.object({
  currentPin: PinLegacy, // accept the legacy 4-digit floor for the verify step
  newPin: Pin,
});

export const LookupFamily = z.object({
  inviteCode: z.string().min(4).max(16),
});

export const UpdateMember = z.object({
  displayName: z.string().min(1).max(40).optional(),
  translation: z.string().optional(),
  voices: z.array(z.string()).optional(),
  notifyTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  schoolDnd: z.boolean().optional(),
  /** Per-kid privacy default — compose flows pre-select this circle.
   *  `null` clears it (back to the family audience). Caller may be the
   *  member themselves OR a parent in the family. */
  defaultShareCircleId: Id.nullable().optional(),
});

/** PATCH /circles/:id/membership — currently just push mute. */
export const UpdateCircleMembership = z.object({
  mutePushAt: z.number().int().nullable().optional(),
});

// ------------------------------------------------------------ memory verse
export const MemoryVerseMode = z.enum([
  'word_bank',
  'first_letters',
  'from_memory',
  'timed_recall',
]);
export type MemoryVerseMode = z.infer<typeof MemoryVerseMode>;

/** Score is integer percent 0..100. The server clamps; the client should too. */
export const RecordMemoryVerseAttempt = z.object({
  mode: MemoryVerseMode,
  score: z.number().int().min(0).max(100),
});

// ----------------------------------------------------------- reading plans
export const PlanSlug = z.string().min(1).max(64);

export const SubscribeReadingPlan = z.object({
  planSlug: PlanSlug,
});

/** `dayNum` is optional — when omitted the route marks `currentDay`. */
export const MarkReadingDay = z.object({
  dayNum: z.number().int().min(1).optional(),
});

// --------------------------------------------------------------- memories
const YyyyMmDd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const CreateMemory = z.object({
  title: z.string().min(1).max(120),
  description: z.string().max(2000).nullable().optional(),
  photoIds: z.array(Id).min(1).max(200),
  coverPhotoId: Id.optional(),
  /** @-mentions in the description (member ids and/or the family
   *  sentinel). The server fans out notifications on create. */
  mentions: Mentions,
  /** YYYY-MM-DD; if omitted, the server defaults to the earliest
   *  takenAt across the supplied photos. */
  eventDate: YyyyMmDd.optional(),
});

export const UpdateMemory = z.object({
  title: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  coverPhotoId: Id.nullable().optional(),
  eventDate: YyyyMmDd.nullable().optional(),
  /** Optional replacement set. When provided, fully replaces photoIds
   *  (caller sends the new full ordering). */
  photoIds: z.array(Id).min(1).max(200).optional(),
});

// -------------------------------------------------------------- sermon notes
/** Upsert a member's sermon note for a given Sunday. The server keys
 *  the upsert on (memberId, date), so re-saving the same date updates
 *  the existing row rather than creating a second one. `body` is
 *  Markdown produced by the WYSIWYG editor. */
export const UpsertSermonNote = z.object({
  date: YyyyMmDd,
  sermonTitle: z.string().max(500).nullable().optional(),
  sermonUrl: z.string().url().max(2000).nullable().optional(),
  body: z.string().max(50000),
  photoIds: z.array(Id).max(50).optional(),
  visibility: z.enum(['private', 'family']).default('family'),
});
export type UpsertSermonNote = z.infer<typeof UpsertSermonNote>;

// ----------------------------------------------------------- WebSocket frames
export const ClientFrame = z.discriminatedUnion('type', [
  z.object({ type: z.literal('send'), body: MessageBody }),
  z.object({ type: z.literal('react'), messageId: Id, reaction: ReactionKind }),
  z.object({ type: z.literal('unreact'), messageId: Id, reaction: ReactionKind }),
  z.object({ type: z.literal('typing'), isTyping: z.boolean() }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientFrame = z.infer<typeof ClientFrame>;

export type ServerFrame =
  | { type: 'hello'; memberId: string; familyId: string }
  | { type: 'message'; message: ServerMessage }
  | { type: 'message_deleted'; messageId: string }
  | {
      type: 'reaction';
      messageId: string;
      memberId: string;
      reaction: ReactionKind;
      added: boolean;
    }
  | { type: 'typing'; memberId: string; isTyping: boolean }
  | { type: 'pong' }
  | { type: 'error'; error: string };

export interface ServerMessage {
  id: string;
  familyId: string;
  authorId: string;
  kind: MessageKind;
  body: MessageBody;
  createdAt: number;
  reactions?: { memberId: string; kind: ReactionKind }[];
}

// ------------------------------------------------------------- voice list
export const VOICES = [
  { key: 'keller', name: 'Tim Keller' },
  { key: 'tripp', name: 'Paul David Tripp' },
  { key: 'wilkin', name: 'Jen Wilkin' },
  { key: 'piper', name: 'John Piper' },
  { key: 'greear', name: 'J.D. Greear' },
  { key: 'lloyd-jones', name: 'Sally Lloyd-Jones' },
  { key: 'baucham', name: 'Voddie Baucham' },
] as const;
export type VoiceKey = (typeof VOICES)[number]['key'];
